import type { ConversaDetalheDTO, MensagemDTO } from '@viniko/shared';
import { Download, FileText, Mic, Play } from 'lucide-react';
import { useState } from 'react';
import { Modal } from '../../componentes/ui';
import { quandoCurto } from '../../lib/tempo';
import { PlayerAudio } from './PlayerAudio';

type Aba = 'midia' | 'docs' | 'audios';

export function Midias({ conversa: c }: { conversa: ConversaDetalheDTO }) {
  const [aberto, setAberto] = useState<Aba | null>(null);
  const [ampliada, setAmpliada] = useState<MensagemDTO | null>(null);

  const comArquivo = c.mensagens.filter((m) => m.midiaUrl && !m.interna).reverse();
  const midia = comArquivo.filter((m) => m.tipo === 'imagem' || m.tipo === 'video');
  const docs = comArquivo.filter((m) => m.tipo === 'documento');
  const audios = comArquivo.filter((m) => m.tipo === 'audio');

  if (!comArquivo.length)
    return <p className="nota-sec">Nenhuma foto, áudio ou arquivo nesta conversa.</p>;

  const grade = (lista: MensagemDTO[]) => (
    <div className="midias-grade">
      {lista.map((m) => (
        <button
          key={m.id}
          className="midia-mini"
          onClick={() => setAmpliada(m)}
          title={m.conteudo || m.midiaNome || ''}
        >
          {m.tipo === 'imagem' ? (
            <img src={m.midiaUrl!} alt={m.midiaNome ?? 'Foto'} loading="lazy" />
          ) : (
            <>
              <video src={m.midiaUrl!} preload="metadata" muted />
              <Play className="midia-play" aria-hidden />
            </>
          )}
        </button>
      ))}
    </div>
  );

  const listaDocs = (lista: MensagemDTO[]) => (
    <ul className="midias-docs">
      {lista.map((m) => (
        <li key={m.id}>
          <a href={`${m.midiaUrl}?baixar=1`} title="Baixar">
            <FileText aria-hidden className="doc-ic" />
            <span>
              <b>{m.midiaNome ?? 'Arquivo'}</b>
              <small>
                {m.atendente?.nomeExibicao ?? (m.autor === 'cliente' ? 'Cliente' : 'IA')} ·{' '}
                {quandoCurto(m.enviadoEm)}
              </small>
            </span>
            <Download aria-hidden className="baixar" />
          </a>
        </li>
      ))}
    </ul>
  );

  const listaAudios = (lista: MensagemDTO[]) => (
    <ul className="midias-docs">
      {lista.map((m) => (
        <li key={m.id} className="audio">
          <Mic aria-hidden className="doc-ic" />
          <span>
            <PlayerAudio src={m.midiaUrl!} />
            <small>
              {m.atendente?.nomeExibicao ?? (m.autor === 'cliente' ? 'Cliente' : 'IA')} ·{' '}
              {quandoCurto(m.enviadoEm)}
            </small>
          </span>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="midias">
      <button
        className="midias-resumo"
        onClick={() => setAberto(midia.length ? 'midia' : docs.length ? 'docs' : 'audios')}
      >
        <span>
          {[
            midia.length &&
              `${midia.length} ${midia.length === 1 ? 'foto/vídeo' : 'fotos e vídeos'}`,
            docs.length && `${docs.length} ${docs.length === 1 ? 'documento' : 'documentos'}`,
            audios.length && `${audios.length} ${audios.length === 1 ? 'áudio' : 'áudios'}`,
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
        <span className="ver-todas">Ver todas</span>
      </button>

      {midia.length > 0 && grade(midia.slice(0, 6))}
      {docs.length > 0 && listaDocs(docs.slice(0, 3))}

      {aberto && (
        <Modal titulo="Mídias da conversa" aoFechar={() => setAberto(null)}>
          <div className="abas-modal" role="tablist">
            {(
              [
                ['midia', `Fotos e vídeos (${midia.length})`],
                ['docs', `Documentos (${docs.length})`],
                ['audios', `Áudios (${audios.length})`],
              ] as const
            ).map(([k, rotulo]) => (
              <button key={k} role="tab" aria-selected={aberto === k} onClick={() => setAberto(k)}>
                {rotulo}
              </button>
            ))}
          </div>
          <div className="midias-modal">
            {aberto === 'midia' &&
              (midia.length ? grade(midia) : <p className="nota-sec">Nenhuma foto ou vídeo.</p>)}
            {aberto === 'docs' &&
              (docs.length ? listaDocs(docs) : <p className="nota-sec">Nenhum documento.</p>)}
            {aberto === 'audios' &&
              (audios.length ? listaAudios(audios) : <p className="nota-sec">Nenhum áudio.</p>)}
          </div>
        </Modal>
      )}

      {ampliada && (
        <div
          className="visor"
          role="dialog"
          aria-label="Mídia ampliada"
          onClick={() => setAmpliada(null)}
        >
          {ampliada.tipo === 'imagem' ? (
            <img src={ampliada.midiaUrl!} alt={ampliada.midiaNome ?? 'Foto'} />
          ) : (
            <video
              src={ampliada.midiaUrl!}
              controls
              autoPlay
              onClick={(e) => e.stopPropagation()}
            />
          )}
          <a
            className="btn"
            href={`${ampliada.midiaUrl}?baixar=1`}
            onClick={(e) => e.stopPropagation()}
          >
            <Download /> Baixar
          </a>
        </div>
      )}
    </div>
  );
}
